import { format } from 'date-fns';
import {
  InfoIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UploadIcon,
} from 'lucide-react';
import React, { useRef, useState } from 'react';

import { Button } from '@/components/chariot-ui';
import { Loader } from '@/components/Loader';
import { MarkdownEditor } from '@/components/markdown/MarkdownEditor';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import {
  useDeleteNarratives,
  useGetNarratives,
  useUploadNarratives,
} from '@/hooks/useNarratives';
import { NarrativesSectionProps } from '@/sections/settings/types';

/**
 * Narratives Section Component
 *
 * Displays a markdown preview of a narratives.md file stored in the
 * customer's S3 bucket, with upload and delete capabilities.
 * Only shown to Praetorian users for ENGAGEMENT type customers.
 *
 * @example
 * ```tsx
 * <NarrativesSection
 *   customerEmail="user@example.com"
 *   isPraetorianUser={true}
 * />
 * ```
 */
export const NarrativesSection: React.FC<NarrativesSectionProps> = ({
  customerEmail,
  isPraetorianUser,
  title = 'Narratives',
  showTitle = true,
  className = '',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');

  const { data, isPending } = useGetNarratives(customerEmail);
  const { mutate: uploadNarratives, isPending: isUploading } =
    useUploadNarratives(customerEmail);
  const { mutate: deleteNarratives, isPending: isDeleting } =
    useDeleteNarratives(customerEmail);

  const content = data?.content ?? '';
  const lastModified = data?.lastModified ?? null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadNarratives(file);
    }
    // Reset input so same file can be re-selected
    event.target.value = '';
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteConfirm = () => {
    deleteNarratives(undefined, {
      onSuccess: () => {
        setIsDeleteModalOpen(false);
      },
    });
  };

  const handleEditClick = () => {
    setEditContent(content);
    setIsEditing(true);
  };

  const handleSave = () => {
    uploadNarratives(editContent, {
      onSuccess: () => {
        setIsEditing(false);
      },
    });
  };

  const handleCancel = () => {
    setEditContent('');
    setIsEditing(false);
  };

  return (
    <div
      className={`border-chariot-stroke bg-chariot-background-primary rounded-lg border ${className}`}
    >
      <div className="p-6">
        {/* Section Header */}
        {showTitle && (
          <div className="mb-4 flex items-center gap-2">
            <h3 className="text-xl font-bold">{title}</h3>
            <Tooltip title="View and manage engagement narratives for this customer">
              <InfoIcon className="text-chariot-text-secondary size-4" />
            </Tooltip>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".md,.txt"
          className="hidden"
          onChange={handleFileSelect}
        />

        {/* Content area */}
        <Loader isLoading={isPending}>
          {content || isEditing ? (
            <div>
              {/* Sub-header bar */}
              <div className="mb-3 flex items-center justify-between">
                <p className="text-chariot-text-secondary text-sm">
                  {isEditing
                    ? 'Editing narratives...'
                    : `Last updated: ${lastModified ? format(lastModified, 'MMM d, yyyy h:mm a') : 'Unknown'}`}
                </p>
                <div className="flex items-center gap-2">
                  {isPraetorianUser && (
                    <>
                      {isEditing ? (
                        <>
                          <Button
                            styleType="tertiary"
                            label="Cancel"
                            size="sm"
                            onClick={handleCancel}
                            disabled={isUploading}
                          />
                          <Button
                            styleType="primary"
                            label={isUploading ? 'Saving...' : 'Save'}
                            size="sm"
                            onClick={handleSave}
                            disabled={isUploading}
                          />
                        </>
                      ) : (
                        <>
                          <Button
                            styleType="tertiary"
                            label="Edit"
                            size="sm"
                            startIcon={<PencilIcon className="size-4" />}
                            onClick={handleEditClick}
                          />
                          <Button
                            styleType="tertiary"
                            label={isUploading ? 'Uploading...' : 'Upload'}
                            size="sm"
                            startIcon={<UploadIcon className="size-4" />}
                            onClick={handleUploadClick}
                            disabled={isUploading}
                          />
                          <Button
                            styleType="tertiary"
                            label={isDeleting ? 'Deleting...' : 'Delete'}
                            size="sm"
                            startIcon={<Trash2Icon className="size-4" />}
                            onClick={() => setIsDeleteModalOpen(true)}
                            disabled={isDeleting}
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Markdown content or editor */}
              {isEditing ? (
                <div className="h-[500px]">
                  <MarkdownEditor
                    value={editContent}
                    onChange={value => setEditContent(value || '')}
                  />
                </div>
              ) : (
                <div className="border-chariot-stroke rounded-md border p-4">
                  <MarkdownPreview source={content} />
                </div>
              )}
            </div>
          ) : (
            /* Empty state */
            <div className="border-chariot-stroke rounded-md border p-8 text-center">
              <p className="text-chariot-text-primary font-medium">
                No narratives uploaded
              </p>
              <p className="text-chariot-text-secondary mt-1 text-sm">
                Upload a narratives.md file to display content here
              </p>
              {isPraetorianUser && (
                <div className="mt-4 flex justify-center gap-2">
                  <Button
                    styleType="tertiary"
                    label="Create"
                    size="sm"
                    startIcon={<PlusIcon className="size-4" />}
                    onClick={handleEditClick}
                  />
                  <Button
                    styleType="tertiary"
                    label={isUploading ? 'Uploading...' : 'Upload'}
                    size="sm"
                    startIcon={<UploadIcon className="size-4" />}
                    onClick={handleUploadClick}
                    disabled={isUploading}
                  />
                </div>
              )}
            </div>
          )}
        </Loader>
      </div>

      {/* Delete confirmation modal */}
      <Modal
        title="Delete Narratives"
        subtitle="Are you sure you want to delete the narratives file? This action cannot be undone."
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        footer={{
          text: isDeleting ? 'Deleting...' : 'Delete',
          onClick: handleDeleteConfirm,
          disabled: isDeleting,
          styleType: 'primary',
        }}
      />
    </div>
  );
};
